
import { LocaleLink } from "@/components/site/LocaleLink";
import { useLocation } from "@tanstack/react-router";
import { ArrowRight, ChevronDown, Menu } from "lucide-react";
import { Button } from "@/components/ui/button";
import { BrandLogo } from "@/components/site/BrandLogo";
import { LocaleSwitcher, useLocaleSwitchHandler, buildLocaleHref } from "@/components/site/LocaleSwitcher";
import { useScrolled } from "@/hooks/use-scrolled";
import { localeFromPath, useT, type Locale } from "@/lib/i18n/I18nProvider";
import { cn } from "@/lib/utils";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger, SheetClose } from "@/components/ui/sheet";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useState } from "react";

type NavChild = { label: string; to?: string };
type NavGroup = { heading: string; items: NavChild[]; viewAll?: NavChild };
type NavItem = {
  label: string;
  to?: string;
  children?: NavChild[];
  groups?: NavGroup[];
};

const LOGIN_URL = "https://mlpn.perceptica.com/en-us/accounts/login/";

const navItems: NavItem[] = [
  {
    label: "Platform",
    to: "/media-monitoring",
    children: [
      { label: "See the platform", to: "/media-monitoring/platform" },
      { label: "Overview", to: "/media-monitoring" },
      { label: "Coverage", to: "/media-monitoring/coverage" },
    ],
  },
  {
    label: "Services",
    to: "/media-analysis",
    children: [
      { label: "Media Analysis", to: "/media-analysis" },
      { label: "Reputation Analysis", to: "/media-analysis/reputation" },
      { label: "Competitive Intelligence", to: "/media-analysis/competitive-intelligence" },
      { label: "Crisis Monitoring & Analysis", to: "/media-analysis/crisis-monitoring" },
      { label: "Market Research", to: "/media-analysis/market-research" },
      { label: "Employer Branding", to: "/media-analysis/employer-branding" },
    ],
  },
  {
    label: "Use Cases",
    to: "/who-we-help",
    groups: [
      {
        heading: "By role",
        items: [
          { label: "Brand Managers", to: "/who-we-help/brand-managers" },
          { label: "PR Managers", to: "/who-we-help/pr-managers" },
          { label: "PR Agencies", to: "/who-we-help/pr-agency" },
          { label: "Media Agencies", to: "/who-we-help/media-agencies" },
          { label: "Employer Branding Experts", to: "/who-we-help/employer-branding-experts" },
        ],
        viewAll: { label: "All roles", to: "/who-we-help" },
      },
      {
        heading: "By industry",
        items: [
          { label: "Automotive", to: "/industries/automotive" },
          { label: "Pharma", to: "/industries/pharma" },
          { label: "Retail", to: "/industries/retail" },
          { label: "Fintech", to: "/industries/fintech" },
          { label: "FMCG", to: "/industries/fmcg" },
        ],
        viewAll: { label: "All industries", to: "/industries" },
      },
    ],
  },
  {
    label: "Insights",
    to: "/blog",
    children: [
      { label: "Reports", to: "/reports" },
      { label: "Blog", to: "/blog" },
    ],
  },
  {
    label: "Company",
    to: "/about-us",
    children: [
      { label: "About", to: "/about-us" },
      { label: "Contact", to: "/contact-us" },
    ],
  },
];

const navItemsBg: NavItem[] = [
  {
    label: "Платформа",
    to: "/media-monitoring",
    children: [
      { label: "Вижте платформата", to: "/media-monitoring/platform" },
      { label: "Преглед", to: "/media-monitoring" },
      { label: "Покритие", to: "/media-monitoring/coverage" },
    ],
  },
  {
    label: "Услуги",
    to: "/media-analysis",
    children: [
      { label: "Медиен анализ", to: "/media-analysis" },
      { label: "Анализ на репутацията", to: "/media-analysis/reputation" },
      { label: "Анализ на конкуренцията", to: "/media-analysis/competitive-intelligence" },
      { label: "Кризисен мониторинг и анализ", to: "/media-analysis/crisis-monitoring" },
      { label: "Анализ на пазара", to: "/media-analysis/market-research" },
      { label: "Анализ на работодателска организация", to: "/media-analysis/employer-branding" },
    ],
  },
  {
    label: "За кого работим",
    to: "/who-we-help",
    groups: [
      {
        heading: "По роля",
        items: [
          { label: "Бранд мениджъри", to: "/who-we-help/brand-managers" },
          { label: "PR мениджъри", to: "/who-we-help/pr-managers" },
          { label: "PR агенции", to: "/who-we-help/pr-agency" },
          { label: "Медийни агенции", to: "/who-we-help/media-agencies" },
          { label: "Експерти по работодателска организация", to: "/who-we-help/employer-branding-experts" },
        ],
        viewAll: { label: "Всички роли", to: "/who-we-help" },
      },
      {
        heading: "По индустрия",
        items: [
          { label: "Автомобилна", to: "/industries/automotive" },
          { label: "Фармацевтична", to: "/industries/pharma" },
          { label: "Търговия на дребно", to: "/industries/retail" },
          { label: "Финтех", to: "/industries/fintech" },
          { label: "FMCG", to: "/industries/fmcg" },
        ],
        viewAll: { label: "Всички индустрии", to: "/industries" },
      },
    ],
  },
  {
    label: "Ресурси",
    to: "/blog",
    children: [
      { label: "Доклади", to: "/reports" },
      { label: "Блог", to: "/blog" },
    ],
  },
  {
    label: "Компания",
    to: "/about-us",
    children: [
      { label: "За нас", to: "/about-us" },
      { label: "Контакти", to: "/contact-us" },
    ],
  },
];

function NavLink({ label, to, dark }: { label: string; to?: string; dark: boolean }) {
  const className = cn(
    "group relative text-[13px] font-medium tracking-[-0.005em] transition-colors duration-200",
    dark ? "text-white/75 hover:text-white" : "text-foreground/75 hover:text-foreground",
  );
  const inner = (
    <>
      {label}
      <span className="absolute -bottom-1 left-0 h-[1.5px] w-0 bg-gradient-brand transition-all duration-300 group-hover:w-full" />
    </>
  );
  if (to) {
    return (
      <LocaleLink to={to} className={className}>
        {inner}
      </LocaleLink>
    );
  }
  return (
    <a href="#" className={className}>
      {inner}
    </a>
  );
}

function NavDropdown({ label, children, dark }: { label: string; children: NavChild[]; dark: boolean }) {
  return (
    <div className="group relative">
      <button
        type="button"
        className={cn(
          "inline-flex items-center gap-1 text-[13px] font-medium tracking-[-0.005em] transition-colors duration-200",
          dark ? "text-white/75 hover:text-white" : "text-foreground/75 hover:text-foreground",
        )}
      >
        {label}
        <ChevronDown className="h-3.5 w-3.5 text-accent transition-transform duration-300 group-hover:rotate-180" />
      </button>
      <div className="invisible absolute left-1/2 top-full z-40 -translate-x-1/2 pt-3 opacity-0 transition-all duration-200 group-hover:visible group-hover:opacity-100 group-focus-within:visible group-focus-within:opacity-100">
        <div className="min-w-[240px] rounded-2xl border border-border/60 bg-background p-2 shadow-soft">
          <ul>
            {children.map((child) =>
              child.to ? (
                <li key={child.label}>
                  <LocaleLink
                    to={child.to}
                    className="block rounded-xl px-4 py-2.5 text-[13px] text-foreground/80 transition-colors hover:bg-secondary hover:text-foreground"
                  >
                    {child.label}
                  </LocaleLink>
                </li>
              ) : (
                <li key={child.label}>
                  <a
                    href="#"
                    className="block rounded-xl px-4 py-2.5 text-[13px] text-foreground/80 transition-colors hover:bg-secondary hover:text-foreground"
                  >
                    {child.label}
                  </a>
                </li>
              ),
            )}
          </ul>
        </div>
      </div>
    </div>
  );
}

function NavMegaDropdown({
  label,
  groups,
  dark,
}: {
  label: string;
  groups: NavGroup[];
  dark: boolean;
}) {
  return (
    <div className="group relative">
      <button
        type="button"
        className={cn(
          "inline-flex items-center gap-1 text-[13px] font-medium tracking-[-0.005em] transition-colors duration-200",
          dark ? "text-white/75 hover:text-white" : "text-foreground/75 hover:text-foreground",
        )}
      >
        {label}
        <ChevronDown className="h-3.5 w-3.5 text-accent transition-transform duration-300 group-hover:rotate-180" />
      </button>
      <div className="invisible absolute left-1/2 top-full z-40 -translate-x-1/2 pt-3 opacity-0 transition-all duration-200 group-hover:visible group-hover:opacity-100 group-focus-within:visible group-focus-within:opacity-100">
        <div className="w-[560px] rounded-2xl border border-border/60 bg-background p-5 shadow-soft">
          <div className="grid grid-cols-2 gap-6">
            {groups.map((group) => (
              <div key={group.heading}>
                <div className="mb-2 px-3 text-[10.5px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
                  {group.heading}
                </div>
                <ul>
                  {group.items.map((child) =>
                    child.to ? (
                      <li key={child.label}>
                        <LocaleLink
                          to={child.to}
                          className="block rounded-xl px-3 py-2 text-[13px] text-foreground/80 transition-colors hover:bg-secondary hover:text-foreground"
                        >
                          {child.label}
                        </LocaleLink>
                      </li>
                    ) : (
                      <li key={child.label}>
                        <a
                          href="#"
                          className="block rounded-xl px-3 py-2 text-[13px] text-foreground/80 transition-colors hover:bg-secondary hover:text-foreground"
                        >
                          {child.label}
                        </a>
                      </li>
                    ),
                  )}
                  {group.viewAll?.to ? (
                    <li className="mt-1 border-t border-border/50 pt-1">
                      <LocaleLink
                        to={group.viewAll.to}
                        className="group/va inline-flex items-center gap-1.5 rounded-xl px-3 py-2 text-[12.5px] font-medium text-accent transition-colors hover:bg-secondary"
                      >
                        {group.viewAll.label}
                        <ArrowRight className="h-3.5 w-3.5 transition-transform duration-200 group-hover/va:translate-x-0.5" />
                      </LocaleLink>
                    </li>
                  ) : null}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

interface SiteHeaderProps {
  /** When true, header starts transparent over a dark hero and becomes solid on scroll */
  transparentOverHero?: boolean;
}

export function SiteHeader({ transparentOverHero = false }: SiteHeaderProps = {}) {
  const scrolled = useScrolled(80);
  const dark = transparentOverHero && !scrolled;
  const { pathname } = useLocation();
  const isBg = localeFromPath(pathname) === "bg";
  const items = isBg ? navItemsBg : navItems;
  const loginLabel = isBg ? "Вход" : "Login";
  const ctaLabel = isBg ? "Заявете демо" : "Request a demo";

  return (
    <>
    <header
      className={cn(
        "left-0 right-0 z-50 isolate transform-gpu border-b backdrop-blur-md transition-colors duration-300",
        // Always fixed-positioned to avoid layout shift when scroll state changes.
        // Only the background/border swap on scroll - the box itself never moves.
        // Near-opaque background prevents partial backdrop-blur repaint patches.
        transparentOverHero
          ? scrolled
            ? "fixed top-0 border-border/60 bg-background/95"
            : "fixed top-0 border-transparent bg-transparent"
          : "sticky top-0 border-border/60 bg-background/95",
      )}
    >
      <div className="mx-auto flex max-w-[1400px] items-center justify-between px-6 py-4 lg:px-10">
        <LocaleLink to="/" aria-label="Perceptica home">
          <BrandLogo size={28} eager wordmarkClassName={dark ? "text-white" : undefined} />
        </LocaleLink>

        <nav key={pathname} className="hidden items-center gap-8 lg:flex" aria-label="Primary">
          {items.map((item) =>
            item.groups ? (
              <NavMegaDropdown key={item.label} label={item.label} groups={item.groups} dark={dark} />
            ) : item.children ? (
              <NavDropdown key={item.label} label={item.label} children={item.children} dark={dark} />
            ) : (
              <NavLink key={item.label} label={item.label} to={item.to} dark={dark} />
            ),
          )}
        </nav>

        <div className="flex items-center gap-3">
          <div className="hidden lg:flex">
            <LocaleSwitcher dark={dark} />
          </div>
          <a
            href={LOGIN_URL}
            className={cn(
              "hidden px-2 text-[13px] font-medium tracking-[-0.005em] transition-colors lg:inline-flex",
              dark ? "text-white/75 hover:text-white" : "text-foreground/75 hover:text-foreground",
            )}
          >
            {loginLabel}
          </a>
          <Button asChild variant={dark ? "outlineLight" : "default"} className="hidden lg:inline-flex">
            <LocaleLink to="/request-a-demo">{ctaLabel}</LocaleLink>
          </Button>
          <MobileMenu items={items} dark={dark} ctaLabel={ctaLabel} loginLabel={loginLabel} isBg={isBg} />
        </div>
      </div>
    </header>
    <MobileFloatingCTA show={scrolled} label={ctaLabel} />
    </>
  );
}

function MobileMenu({
  items,
  dark,
  ctaLabel,
  loginLabel,
  isBg,
}: {
  items: NavItem[];
  dark: boolean;
  ctaLabel: string;
  loginLabel: string;
  isBg: boolean;
}) {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const { locale } = useT();
  const handleLocale = useLocaleSwitchHandler();
  const close = () => setOpen(false);

  const langLabel = isBg ? "Език" : "Language";
  const menuLabel = isBg ? "Меню" : "Menu";

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <button
          type="button"
          aria-label={menuLabel}
          className={cn(
            "inline-flex h-10 w-10 items-center justify-center rounded-full border transition-colors lg:hidden",
            dark
              ? "border-white/20 text-white hover:bg-white/10"
              : "border-border/70 text-foreground hover:bg-secondary",
          )}
        >
          <Menu className="h-5 w-5" />
        </button>
      </SheetTrigger>
      <SheetContent side="right" className="w-[88vw] max-w-sm overflow-y-auto p-0">
        <SheetHeader className="border-b border-border/60 px-5 py-4 text-left">
          <SheetTitle className="text-base">{menuLabel}</SheetTitle>
        </SheetHeader>
        <div className="flex flex-col gap-1 px-3 py-3">
          <Accordion type="multiple" className="w-full">
            {items.map((item) => {
              const flat: NavChild[] = item.groups
                ? item.groups.flatMap((g) => [
                    ...g.items,
                    ...(g.viewAll ? [g.viewAll] : []),
                  ])
                : item.children ?? [];
              if (flat.length === 0) {
                return (
                  <div key={item.label} className="py-1">
                    {item.to ? (
                      <SheetClose asChild>
                        <LocaleLink
                          to={item.to}
                          className="block rounded-lg px-3 py-2.5 text-[14px] font-medium text-foreground hover:bg-secondary"
                        >
                          {item.label}
                        </LocaleLink>
                      </SheetClose>
                    ) : (
                      <span className="block px-3 py-2.5 text-[14px] font-medium">{item.label}</span>
                    )}
                  </div>
                );
              }
              return (
                <AccordionItem key={item.label} value={item.label} className="border-b-0">
                  <AccordionTrigger className="rounded-lg px-3 py-2.5 text-[14px] font-medium hover:bg-secondary hover:no-underline">
                    {item.label}
                  </AccordionTrigger>
                  <AccordionContent className="pb-1">
                    <ul className="ml-2 flex flex-col gap-0.5 border-l border-border/60 pl-2">
                      {flat.map((child) =>
                        child.to ? (
                          <li key={child.label}>
                            <SheetClose asChild>
                              <LocaleLink
                                to={child.to}
                                className="block rounded-md px-3 py-2 text-[13px] text-foreground/80 hover:bg-secondary hover:text-foreground"
                              >
                                {child.label}
                              </LocaleLink>
                            </SheetClose>
                          </li>
                        ) : null,
                      )}
                    </ul>
                  </AccordionContent>
                </AccordionItem>
              );
            })}
          </Accordion>
        </div>

        <div className="border-t border-border/60 px-5 py-4">
          <div className="mb-2 text-[10.5px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
            {langLabel}
          </div>
          <div className="inline-flex items-center gap-1 rounded-full border border-border/70 px-2 py-1 text-[12px] font-semibold tracking-[0.08em]">
            {(["en", "bg"] as Locale[]).map((target) => (
              <button
                key={target}
                type="button"
                onClick={(e) => {
                  handleLocale(target)(e);
                  if (!e.defaultPrevented) return;
                  close();
                }}
                aria-pressed={locale === target}
                className={cn(
                  "rounded-full px-2.5 py-1 transition-colors",
                  locale === target ? "bg-secondary text-foreground" : "text-foreground/70 hover:text-foreground",
                )}
              >
                {target === "en" ? "EN" : "БГ"}
              </button>
            ))}
          </div>
        </div>

        <div className="flex flex-col gap-2 border-t border-border/60 px-5 py-4">
          <SheetClose asChild>
            <Button asChild className="w-full">
              <LocaleLink to="/request-a-demo">{ctaLabel}</LocaleLink>
            </Button>
          </SheetClose>
          <a
            href={LOGIN_URL}
            className="text-center text-[13px] font-medium text-foreground/70 hover:text-foreground"
            onClick={close}
          >
            {loginLabel}
          </a>
        </div>
      </SheetContent>
    </Sheet>
  );
}

function MobileFloatingCTA({ show, label }: { show: boolean; label: string }) {
  return (
    <div
      className={cn(
        "fixed bottom-4 right-4 z-50 transition-opacity duration-300 lg:hidden",
        show ? "opacity-100" : "pointer-events-none opacity-0",
      )}
    >
      <Button asChild className="shadow-pill">
        <LocaleLink to="/request-a-demo">{label}</LocaleLink>
      </Button>
    </div>
  );
}
